import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Paper, Snackbar } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import LeasecontrolUsuariosToolbar from "../../leasecontrol/components/LeasecontrolUsuariosToolbar";
import LeasecontrolUsuariosTable from "../../leasecontrol/components/LeasecontrolUsuariosTable";
import LeasecontrolUsuarioDialog from "../../leasecontrol/components/LeasecontrolUsuarioDialog";
import ConfirmDeleteDialog from "../../leasecontrol/components/ConfirmDeleteDialog";
import UsuarioIntegracionDetalleDialog from "../../leasecontrol/components/UsuarioIntegracionDetalleDialog";
import WialonUserEditDialog from "../components/WialonUserEditDialog";
import { createWialonUser, deleteWialonUser, getWialonUserDetalle, listWialonUsers } from "../services/wialonUsers";

export default function WialonUsersPage() {
  const debounceRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupBy, setGroupBy] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [del, setDel] = useState({ open: false, user: null, loading: false });
  const [detalle, setDetalle] = useState({ open: false, loading: false, data: null });
  const [snack, setSnack] = useState({ open: false, msg: "", type: "success" });

  const fetchRows = async ({
    usePage = page,
    useSearch = search,
    useLimit = limit,
    useGroupBy = groupBy,
  } = {}) => {
    setErr("");
    setLoadingList(true);
    try {
      const resp = await listWialonUsers({
        search: useSearch,
        page: usePage,
        limit: useLimit,
        groupBy: useGroupBy ? 1 : 0,
      });
      const payload = resp?.data || {};

      if (useGroupBy) {
        setGroups(payload?.data || []);
        setRows([]);
      } else {
        setRows(payload?.data || []);
        setGroups([]);
      }

      setTotalPages(payload?.totalPages || 1);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Error listando usuarios Wialon");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, groupBy]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchRows({ usePage: 1, useSearch: search, useLimit: limit, useGroupBy: groupBy });
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleUpdated = () => {
    setSnack({ open: true, type: "success", msg: "Cambios guardados." });
    fetchRows();
  };

  const handleCreated = () => {
    setSnack({ open: true, type: "success", msg: "Usuario Wialon creado correctamente." });
    fetchRows({ usePage: 1, useSearch: search, useLimit: limit, useGroupBy: groupBy });
    setPage(1);
  };

  const handleDeleteConfirm = async () => {
    const u = del.user;
    const itemId = u?.id ?? u?.userId ?? u?.itemId;

    if (!itemId) {
      setSnack({ open: true, type: "error", msg: "No se encontro itemId." });
      return;
    }

    try {
      setDel((s) => ({ ...s, loading: true }));
      await deleteWialonUser(Number(itemId));
      setSnack({ open: true, type: "success", msg: "Eliminado correctamente." });
      setDel({ open: false, user: null, loading: false });
      fetchRows();
    } catch (e) {
      setSnack({
        open: true,
        type: "error",
        msg: e?.response?.data?.error || e?.response?.data?.message || e.message || "Error eliminando",
      });
      setDel((s) => ({ ...s, loading: false }));
    }
  };

  const handleOpenDetalle = async (u) => {
    const itemId = u?.id ?? u?.userId ?? u?.itemId;
    if (!itemId) {
      setSnack({ open: true, type: "error", msg: "No se encontro itemId." });
      return;
    }

    setDetalle({ open: true, loading: true, data: null });
    try {
      const resp = await getWialonUserDetalle(Number(itemId));
      setDetalle({ open: true, loading: false, data: resp?.data || null });
    } catch (e) {
      setSnack({ open: true, type: "error", msg: e?.response?.data?.error || e.message || "No se pudo cargar el detalle." });
      setDetalle({ open: false, loading: false, data: null });
    }
  };

  const showPagination = useMemo(() => totalPages > 1, [totalPages]);

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 1.25, md: 2 }, minHeight: "calc(100vh - 64px)", bgcolor: "background.default" }}>
        <PageHeader title="Gestion de Usuarios" subtitle="Modulo de Wialon" backTo=".." sx={{ mb: 1 }} />

        <LeasecontrolUsuariosToolbar
          search={search}
          onSearchChange={setSearch}
          limit={limit}
          onLimitChange={(v) => {
            setLimit(v);
            setPage(1);
          }}
          onRefresh={() => fetchRows({ usePage: page, useSearch: search, useLimit: limit, useGroupBy: groupBy })}
          onCreate={() => setCreateOpen(true)}
          loading={loadingList}
          groupBy={groupBy}
          onGroupByChange={(v) => {
            setGroupBy(v);
            setPage(1);
          }}
        />

        {!!err && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {err}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 0.75, mt: 1, opacity: loadingList ? 0.6 : 1 }}>
          <LeasecontrolUsuariosTable
            rows={!groupBy ? rows : []}
            groups={groupBy ? groups : null}
            idLabel="Wialon ID"
            onEdit={(u) => setSelected(u)}
            onDelete={(u) => setDel({ open: true, user: u, loading: false })}
            onDetail={handleOpenDetalle}
          />
        </Paper>

        {showPagination && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <Pagination size="small" count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
          </Box>
        )}

        <WialonUserEditDialog
          open={!!selected}
          onClose={() => setSelected(null)}
          user={selected}
          onUpdated={handleUpdated}
        />

        <LeasecontrolUsuarioDialog
          open={createOpen}
          mode="create"
          providerName="Wialon"
          createUsuario={createWialonUser}
          onClose={() => setCreateOpen(false)}
          onSuccess={handleCreated}
        />

        <ConfirmDeleteDialog
          open={del.open}
          loading={del.loading}
          description={`Seguro que deseas eliminar el usuario Wialon ID ${del.user?.id ?? del.user?.userId ?? del.user?.itemId}?`}
          onClose={() => setDel({ open: false, user: null, loading: false })}
          onConfirm={handleDeleteConfirm}
        />

        <UsuarioIntegracionDetalleDialog
          open={detalle.open}
          loading={detalle.loading}
          data={detalle.data}
          onClose={() => setDetalle({ open: false, loading: false, data: null })}
        />

        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert
            severity={snack.type}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
}
